"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { type FormEvent, useState } from "react";
import { employeeAccessDeniedPath } from "@/lib/auth/employee-session";
import type { AuthLoginMethod } from "@/lib/auth/login-method";
import { getRedirectPath } from "@/lib/auth/redirects";
import { useSupabaseClient } from "@/lib/supabase/client";

export type AuthFormMode = "reset-password" | "sign-in" | "sign-up" | "update-password";

const authCopy = {
  "sign-in": {
    title: "Sign in",
    description: "Use your TCOS operator account.",
    button: "Sign in",
    error: "We could not sign you in. Try again or contact support.",
  },
  "sign-up": {
    title: "Create account",
    description: "Create a preview operator account.",
    button: "Create account",
    error: "We could not create your account. Try again or contact support.",
  },
  "reset-password": {
    title: "Reset password",
    description: "Send a recovery link to your email address.",
    button: "Send reset link",
    error: "We could not send a reset link. Try again or contact support.",
  },
  "update-password": {
    title: "Set new password",
    description: "Choose a new password for your account.",
    button: "Update password",
    error: "We could not update your password. Try again or contact support.",
  },
} satisfies Record<
  AuthFormMode,
  { button: string; description: string; error: string; title: string }
>;

const safeStatusMessages: Record<string, string> = {
  confirmation_failed: "We could not confirm that link. Request a new link and try again.",
  confirmation_missing_code: "That confirmation link is invalid. Request a new link and try again.",
  session_revoked: "Your access changed and this session was ended. Sign in again to continue.",
};

export type EnterpriseSsoTarget = { domain: string } | { providerId: string };

const inputClassName =
  "h-11 w-full rounded-md border border-input bg-card px-3.5 text-foreground shadow-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring/30";
const buttonClassName =
  "h-11 w-full rounded-md bg-primary px-4 font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-65";
const linkClassName = "font-semibold text-primary hover:underline";

function getSafeStatusMessage(searchParams: URLSearchParams): string | null {
  const status = searchParams.get("status");
  return status ? (safeStatusMessages[status] ?? null) : null;
}

function getCopy(loginMethod: AuthLoginMethod, mode: AuthFormMode) {
  if (mode === "sign-in" && loginMethod === "saml") {
    return {
      ...authCopy["sign-in"],
      button: "Continue with Microsoft",
      description: "Use your company Microsoft account. Access is assigned by your IT department.",
      error: "We could not start company sign-in. Try again or contact your IT department.",
    };
  }

  return authCopy[mode];
}

export function AuthForm({
  loginMethod,
  mode,
  ssoTarget = null,
}: {
  loginMethod: AuthLoginMethod;
  mode: AuthFormMode;
  ssoTarget?: EnterpriseSsoTarget | null;
}) {
  const searchParams = useSearchParams();
  const redirectPath = getRedirectPath(searchParams);
  const supabase = useSupabaseClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [message, setMessage] = useState<string | null>(getSafeStatusMessage(searchParams));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const copy = getCopy(loginMethod, mode);
  const isEnterpriseSignIn = mode === "sign-in" && loginMethod === "saml";

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsPending(true);
    setErrorMessage(null);
    setMessage(null);

    try {
      if (isEnterpriseSignIn) {
        if (!ssoTarget) {
          throw new Error("Enterprise SSO is not configured");
        }
        // Enterprise SAML 2.0 through the Supabase SSO provider. The callback
        // runs the employee login step (JIT, mapped membership, or hard deny).
        const { data, error } = await supabase.auth.signInWithSSO({
          ...ssoTarget,
          options: {
            redirectTo: `${window.location.origin}/auth/confirm?next=${encodeURIComponent(
              redirectPath
            )}`,
            skipBrowserRedirect: true,
          },
        });

        if (error || !data?.url) throw error ?? new Error("Enterprise SSO returned no URL");
        window.location.assign(data.url);
        return;
      }

      if (mode === "sign-in") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        window.location.assign(redirectPath);
        return;
      }

      if (mode === "sign-up") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: fullName ? { full_name: fullName } : undefined,
            emailRedirectTo: `${window.location.origin}/auth/confirm?next=${encodeURIComponent(
              redirectPath
            )}`,
          },
        });

        if (error) throw error;
        if (data.session) {
          window.location.assign(redirectPath);
          return;
        }

        setMessage("Check your email to confirm your account before signing in.");
        return;
      }

      if (mode === "reset-password") {
        const updatePasswordPath = `/update-password?next=${encodeURIComponent(redirectPath)}`;
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/confirm?next=${encodeURIComponent(
            updatePasswordPath
          )}`,
        });

        if (error) throw error;
        setMessage("Check your email for a password reset link.");
        return;
      }

      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      window.location.assign(redirectPath);
    } catch {
      setErrorMessage(copy.error);
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="w-full rounded-lg border border-border bg-card p-6 shadow-sm">
      <div className="mb-6">
        <h2 className="font-heading text-3xl tracking-tight">{copy.title}</h2>
        <p className="mt-2 text-muted-foreground text-sm">{copy.description}</p>
      </div>

      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        {mode === "sign-up" ? (
          <label className="flex flex-col gap-2 font-medium text-sm">
            Full name
            <input
              autoComplete="name"
              className={inputClassName}
              name="name"
              onChange={(event) => setFullName(event.target.value)}
              type="text"
              value={fullName}
            />
          </label>
        ) : null}

        {mode !== "update-password" && !isEnterpriseSignIn ? (
          <label className="flex flex-col gap-2 font-medium text-sm">
            Email
            <input
              autoComplete="email"
              className={inputClassName}
              name="email"
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </label>
        ) : null}

        {mode !== "reset-password" && !isEnterpriseSignIn ? (
          <label className="flex flex-col gap-2 font-medium text-sm">
            {mode === "update-password" ? "New password" : "Password"}
            <input
              autoComplete={mode === "sign-in" ? "current-password" : "new-password"}
              className={inputClassName}
              name="password"
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </label>
        ) : null}

        {errorMessage ? (
          <p className="rounded-md border border-destructive/30 bg-destructive-container p-3 text-destructive-container-foreground text-sm">
            {errorMessage}
          </p>
        ) : null}
        {message ? (
          <p className="rounded-md border border-primary/20 bg-primary/10 p-3 text-primary text-sm">
            {message}
          </p>
        ) : null}

        <button className={buttonClassName} disabled={isPending} type="submit">
          {isPending ? "Working..." : copy.button}
        </button>
      </form>

      {mode === "sign-in" && loginMethod === "password" ? (
        <div className="mt-6 flex items-center justify-between gap-3 text-sm">
          <Link
            className={linkClassName}
            href={`/sign-up?redirect_to=${encodeURIComponent(redirectPath)}`}
          >
            Create account
          </Link>
          <Link
            className={linkClassName}
            href={`/reset-password?redirect_to=${encodeURIComponent(redirectPath)}`}
          >
            Forgot password?
          </Link>
        </div>
      ) : mode !== "sign-in" ? (
        <Link
          className={`mt-6 inline-block text-sm ${linkClassName}`}
          href={`/sign-in?redirect_to=${encodeURIComponent(redirectPath)}`}
        >
          Back to sign in
        </Link>
      ) : isEnterpriseSignIn ? (
        <p className="mt-6 text-muted-foreground text-xs leading-5">
          No account is created here. If sign-in succeeds but you are told access is not assigned,
          your IT department controls which groups reach TCOS.{" "}
          <Link className={linkClassName} href={employeeAccessDeniedPath}>
            Learn more
          </Link>
        </p>
      ) : null}
    </div>
  );
}
